import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import daCommon from "@/locales/da/common.json";
import enCommon from "@/locales/en/common.json";

/**
 * i18n foundation (#104, phases 1–2), pinned to English for now (#952).
 *
 * - Namespaced JSON resources under `src/locales/<lng>/<ns>.json`; `common` is
 *   the default namespace. Resources are bundled statically while the catalog
 *   is small — switch to lazy backend loading when Phase 3 grows it.
 * - English only (#952): only a handful of strings were translated, so the
 *   active language is fixed to {@link ACTIVE_LANGUAGE}. Nothing detects or
 *   applies another language — not the browser, not the old `portal.language`
 *   localStorage cache, not a stored `preferences.language` of "da". The
 *   Settings language picker is shown but disabled. The resources, the `t()`
 *   calls and `SUPPORTED_LANGUAGES` stay so the next phase only has to
 *   re-enable detection/selection.
 * - `en` is the fallback and source of truth for copy.
 */

export const SUPPORTED_LANGUAGES = ["en", "da"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** The language the whole app renders in until more languages ship (#952). */
export const ACTIVE_LANGUAGE: SupportedLanguage = "en";

export const isSupportedLanguage = (value: unknown): value is SupportedLanguage =>
  typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

i18n.use(initReactI18next).init({
  resources: {
    en: { common: enCommon },
    da: { common: daCommon },
  },
  lng: ACTIVE_LANGUAGE,
  defaultNS: "common",
  supportedLngs: [...SUPPORTED_LANGUAGES],
  fallbackLng: "en",
  interpolation: {
    // React already escapes rendered strings.
    escapeValue: false,
  },
  react: {
    // Resources are bundled, so nothing suspends; keeps tests simple.
    useSuspense: false,
  },
});

// Keep <html lang> in sync with the active language so screen readers apply
// the right pronunciation rules to translated content (#849 review).
const syncDocumentLanguage = (lng?: string) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = isSupportedLanguage(lng) ? lng : "en";
  }
};
i18n.on("languageChanged", syncDocumentLanguage);
syncDocumentLanguage(i18n.resolvedLanguage);

export { i18n };
